import { memo, useEffect, useMemo, useState } from "react";
import { useQuery } from "@rocicorp/zero/react";
import { queries } from "@/lib/queries";
import { useCurrentUser } from "@/lib/current-user";
import { TextShimmer } from "@/components/ui/text-shimmer";

const TYPING_TIMEOUT_MS = 5000;

/**
 * Self-contained typing indicator. Owns its own Zero query and 1-second
 * interval — parent never re-renders for typing.
 *
 * When someone types, only this ~30-line component re-renders.
 * The rest of MessageArea is completely isolated.
 */
export const TypingIndicator = memo(function TypingIndicator({
  channelId,
  hidden = false,
}: {
  channelId: string;
  /** True when the hosting MessageArea is multiplexed and not active.
   *  Skips the 1s refresh interval on kept-alive instances. */
  hidden?: boolean;
}) {
  const { user } = useCurrentUser();

  // Phase 1.5.3 — typing source moved from users.typing(channelId) to
  // user_presence.typing(channelId). Each row is a user_presence entry
  // (user_id / typing_at) with the joined `.related("user")` providing
  // display_name + identity.
  const [typingPresenceRaw] = useQuery(
    channelId ? queries.user_presence.typing(channelId) : null,
  );
  const typingPresence = useMemo(
    () => typingPresenceRaw ?? [],
    [typingPresenceRaw],
  );

  // 1-second refresh timer — only runs while someone is actively typing
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (hidden) return;
    const hasActive = typingPresence.some(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (p: any) => typeof p.typing_at === "number",
    );
    if (!hasActive) return;

    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [typingPresence, hidden]);

  // Filter to active typers (not self, within timeout)
  const activeTypers = typingPresence.filter(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (p: any) =>
      p.user_id !== user?.id &&
      p.typing_at &&
      now - p.typing_at < TYPING_TIMEOUT_MS,
  );

  const humanTypers: string[] = activeTypers
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .map((p: any) => p.user?.display_name as string | undefined)
    .filter((name): name is string => Boolean(name));
  let typingText = "";
  if (humanTypers.length === 1) typingText = `${humanTypers[0]} is typing...`;
  else if (humanTypers.length > 1)
    typingText = `${humanTypers.join(", ")} are typing...`;

  // Zero-height container — takes no layout space. Content overlays
  // upward via absolute positioning, preventing layout shift when
  // the typing indicator appears/disappears.
  return (
    <div className="h-0 relative z-10">
      {typingText && (
        <div className="absolute bottom-0 left-0 right-0 px-4 pb-1 pt-3 bg-gradient-to-t from-background from-60% to-transparent">
          <TextShimmer
            className="text-[13px] text-muted-foreground"
            duration={1.8}
            spread={22}
          >
            {typingText}
          </TextShimmer>
        </div>
      )}
    </div>
  );
});
